import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, ShoppingBag, DollarSign, Loader2, RefreshCw, Package } from "lucide-react";

function PendingETransfers() {
    const [orders, setOrders] = useState([]);
    const [confirming, setConfirming] = useState(null);
    const [manualId, setManualId] = useState(null);
    const [mf, setMf] = useState({ card_code: "", pin: "", note: "", expiry: "", redemption_url: "" });
    const [mfSaving, setMfSaving] = useState(false);

    const manualDeliver = async (id) => {
        if (!mf.card_code.trim()) { alert("Card code is required"); return; }
        setMfSaving(true);
        try {
            await api.post(`/admin/prepaidforge/manual-fulfill/${id}`, mf);
            setManualId(null);
            setMf({ card_code: "", pin: "", note: "", expiry: "", redemption_url: "" });
            load();
            alert("Card delivered to customer!");
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to fulfill");
        } finally { setMfSaving(false); }
    };

    const load = () => {
        api.get("/admin/prepaidforge/pending-etransfers")
            .then(r => setOrders(r.data))
            .catch(() => {});
    };

    useEffect(() => { load(); }, []);

    const confirm = async (id) => {
        if (!window.confirm("Confirm e-transfer received and deliver gift card code?")) return;
        setConfirming(id);
        try {
            await api.post(`/admin/prepaidforge/confirm-etransfer/${id}`);
            load();
            alert("Code delivered to customer!");
        } catch (err) {
            alert(err.response?.data?.detail || "Failed");
        } finally { setConfirming(null); }
    };

    if (orders.length === 0) return null;

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                <h3 className="font-semibold text-amber-900">Pending E-Transfer Payments ({orders.length})</h3>
            </div>
            <div className="divide-y divide-amber-100">
                {orders.map(o => (
                    <div key={o.id} className="px-5 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div className="font-mono text-xs text-slate-500">{o.id}</div>
                            <div className="font-semibold text-slate-900">{o.sku}</div>
                            <div className="text-xs text-slate-500">{o.user_email}</div>
                        </div>
                        <div className="text-right">
                            <div className="font-bold text-slate-900">CA${Number(o.price_cad||0).toFixed(2)}</div>
                            <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => confirm(o.id)} disabled={confirming === o.id}
                                className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-xl text-sm flex items-center gap-2 whitespace-nowrap justify-center">
                                {confirming === o.id ? <Loader2 size={14} className="animate-spin" /> : "Auto-Deliver"}
                            </button>
                            <button onClick={() => setManualId(manualId === o.id ? null : o.id)}
                                className="bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold px-4 py-2 rounded-xl text-sm whitespace-nowrap">
                                Fulfill Manually
                            </button>
                        </div>
                    </div>
                    {manualId === o.id && (
                        <div className="mt-3 bg-white border border-amber-100 rounded-xl p-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-slate-600">Card Code / Number *</label>
                                <input value={mf.card_code} onChange={e => setMf(f => ({ ...f, card_code: e.target.value }))}
                                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="XXXX-XXXX-XXXX" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-slate-600">PIN (optional)</label>
                                    <input value={mf.pin} onChange={e => setMf(f => ({ ...f, pin: e.target.value }))}
                                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="1234" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Expiry (optional)</label>
                                    <input value={mf.expiry} onChange={e => setMf(f => ({ ...f, expiry: e.target.value }))}
                                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="12/2027" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600">Redemption URL (optional)</label>
                                <input value={mf.redemption_url} onChange={e => setMf(f => ({ ...f, redemption_url: e.target.value }))}
                                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600">Note / Instructions (optional)</label>
                                <textarea value={mf.note} onChange={e => setMf(f => ({ ...f, note: e.target.value }))}
                                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="How to redeem..." />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => manualDeliver(o.id)} disabled={mfSaving}
                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
                                    {mfSaving ? <Loader2 size={14} className="animate-spin" /> : "Deliver Card"}
                                </button>
                                <button onClick={() => setManualId(null)} disabled={mfSaving}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-lg text-sm">Cancel</button>
                            </div>
                        </div>
                    )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function BuyCardTraffic() {
    const [a, setA] = useState(null);
    useEffect(() => {
        api.get("/admin/buygiftcard/analytics").then(r => setA(r.data)).catch(() => {});
    }, []);
    if (!a) return null;
    const max = Math.max(...(a.days || []).map(x => x.visits), 1);
    const stat = (label, val, sub) => (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xl font-bold text-slate-900">{val}</div>
            {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <h3 className="font-semibold text-slate-900">Buy Gift Card Traffic</h3>
                <span className="ml-auto text-sm font-bold text-emerald-600">{a.online_now} online now</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {stat("Today Visits", a.today_visits, `${a.today_unique} unique`)}
                {stat("Today Buy Clicks", a.today_buy_clicks)}
                {stat("Total Visits", a.total_visits, `${a.total_unique_visitors} unique`)}
                {stat("Total Buy Clicks", a.total_buy_clicks, `${a.total_orders} orders`)}
            </div>
            <div className="space-y-1">
                <div className="text-xs font-semibold text-slate-500 mb-2">Last 7 Days</div>
                {(a.days || []).map((d, i) => {
                    const pct = Math.max((d.visits / max) * 100, 2);
                    return (
                        <div key={i} className="flex items-center gap-3">
                            <div className="text-xs text-slate-400 w-12 flex-shrink-0">{d.label}</div>
                            <div className="flex-1 bg-slate-200 rounded-full h-5 overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500 flex items-center justify-end pr-2" style={{ width: `${pct}%` }}>
                                    {d.visits > 0 && <span className="text-white text-xs font-bold">{d.visits}</span>}
                                </div>
                            </div>
                            <div className="text-xs text-slate-400 w-20 flex-shrink-0">{d.buy_clicks} clicks</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AwaitingStock() {
    const [orders, setOrders] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [form, setForm] = useState({ card_code: "", pin: "", note: "", expiry: "", redemption_url: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.get("/admin/prepaidforge/awaiting-stock")
            .then(r => setOrders(r.data))
            .catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const openForm = (id) => {
        setOpenId(id);
        setForm({ card_code: "", pin: "", note: "", expiry: "", redemption_url: "" });
    };

    const submit = async (id) => {
        if (!form.card_code.trim()) { alert("Card code is required"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/prepaidforge/manual-fulfill/${id}`, form);
            setOpenId(null);
            load();
            alert("Card delivered to customer!");
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to fulfill");
        } finally { setSaving(false); }
    };

    if (orders.length === 0) return null;

    return (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-blue-200 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                <h3 className="font-semibold text-blue-900">Needs Manual Fulfillment ({orders.length})</h3>
                <span className="ml-auto text-xs text-blue-700">Paid orders PrepaidForge could not deliver</span>
            </div>
            <div className="divide-y divide-blue-100">
                {orders.map(o => (
                    <div key={o.id} className="px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="font-mono text-xs text-slate-500">{o.id}</div>
                                <div className="font-semibold text-slate-900">{o.sku}</div>
                                <div className="text-xs text-slate-500">{o.user_email}</div>
                            </div>
                            <div className="text-right">
                                <div className="font-bold text-slate-900">${Number(o.price_usd || o.price || 0).toFixed(2)}</div>
                                <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString()}</div>
                            </div>
                            {openId !== o.id && (
                                <button onClick={() => openForm(o.id)}
                                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-4 py-2 rounded-xl text-sm whitespace-nowrap">
                                    Fulfill Manually
                                </button>
                            )}
                        </div>
                        {openId === o.id && (
                            <div className="mt-4 bg-white border border-blue-100 rounded-xl p-4 space-y-3">
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Card Code / Number *</label>
                                    <input value={form.card_code} onChange={e => setForm(f => ({ ...f, card_code: e.target.value }))}
                                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="XXXX-XXXX-XXXX" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-medium text-slate-600">PIN (optional)</label>
                                        <input value={form.pin} onChange={e => setForm(f => ({ ...f, pin: e.target.value }))}
                                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="1234" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-slate-600">Expiry (optional)</label>
                                        <input value={form.expiry} onChange={e => setForm(f => ({ ...f, expiry: e.target.value }))}
                                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="12/2027" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Redemption URL (optional)</label>
                                    <input value={form.redemption_url} onChange={e => setForm(f => ({ ...f, redemption_url: e.target.value }))}
                                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Note / Instructions (optional)</label>
                                    <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                                        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="How to redeem..." />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => submit(o.id)} disabled={saving}
                                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
                                        {saving ? <Loader2 size={14} className="animate-spin" /> : "Deliver Card"}
                                    </button>
                                    <button onClick={() => setOpenId(null)} disabled={saving}
                                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-lg text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function AllOrders() {
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [method, setMethod] = useState("");
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (status) params.set("status", status);
        if (method) params.set("payment_method", method);
        api.get(`/admin/prepaidforge/orders?${params.toString()}`)
            .then(r => { setOrders(r.data.orders || []); setLoading(false); })
            .catch(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const badge = (st) => {
        if (st === "completed") return "bg-emerald-50 text-emerald-700";
        if (st === "awaiting_stock") return "bg-blue-50 text-blue-700";
        if (st === "pending_payment") return "bg-amber-50 text-amber-700";
        if (st === "expired") return "bg-slate-100 text-slate-500";
        return "bg-slate-50 text-slate-600";
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-3">All Orders - Search & Support</h3>
                <div className="flex flex-wrap gap-2">
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && load()}
                        placeholder="Search order ID, email, or card..."
                        className="flex-1 min-w-[200px] border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                    <select value={status} onChange={e => setStatus(e.target.value)}
                        className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        <option value="">All statuses</option>
                        <option value="completed">Completed</option>
                        <option value="pending_payment">Pending Payment</option>
                        <option value="awaiting_stock">Awaiting Stock</option>
                        <option value="expired">Expired</option>
                    </select>
                    <select value={method} onChange={e => setMethod(e.target.value)}
                        className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        <option value="">All methods</option>
                        <option value="stripe">Stripe</option>
                        <option value="etransfer">E-Transfer</option>
                        <option value="paypal">PayPal</option>
                        <option value="wallet">Wallet</option>
                    </select>
                    <button onClick={load}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                        Search
                    </button>
                </div>
            </div>
            {loading ? (
                <div className="p-8 text-center"><Loader2 size={24} className="animate-spin text-emerald-500 mx-auto" /></div>
            ) : orders.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No orders found</div>
            ) : (
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                        <th className="text-left px-4 py-3">Order ID</th>
                        <th className="text-left px-4 py-3">Customer</th>
                        <th className="text-left px-4 py-3">Card</th>
                        <th className="text-right px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3">Method</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3">Code</th>
                        <th className="text-left px-4 py-3">Date</th>
                    </tr></thead>
                    <tbody>
                        {orders.map(o => (
                            <>
                            <tr key={o.id} onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                                <td className="px-4 py-3 font-mono text-xs text-slate-600">{o.id}</td>
                                <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">{o.user_email}</td>
                                <td className="px-4 py-3 text-slate-700 max-w-[140px] truncate">{o.sku}</td>
                                <td className="px-4 py-3 text-right font-semibold">{o.price_cad ? `CA$${Number(o.price_cad).toFixed(2)}` : `${Number(o.price||0).toFixed(2)}`}</td>
                                <td className="px-4 py-3 capitalize text-slate-600">{o.payment_method}</td>
                                <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${badge(o.status)}`}>{o.status}</span></td>
                                <td className="px-4 py-3">{o.has_code ? <span className="text-emerald-600 text-xs font-semibold">Delivered</span> : <span className="text-slate-400 text-xs">-</span>}</td>
                                <td className="px-4 py-3 text-xs text-slate-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "-"}</td>
                            </tr>
                            {expanded === o.id && (
                                <tr key={o.id + "-detail"} className="bg-slate-50 border-t border-slate-100">
                                    <td colSpan={8} className="px-4 py-4">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                            <div><div className="text-slate-400">User ID</div><div className="font-mono text-slate-700">{o.user_id || "-"}</div></div>
                                            <div><div className="text-slate-400">Code delivered</div><div className="font-mono text-slate-700 break-all">{o.manual_code || o.code || "-"}</div></div>
                                            <div><div className="text-slate-400">PIN</div><div className="font-mono text-slate-700">{o.manual_pin || "-"}</div></div>
                                            <div><div className="text-slate-400">Fulfilled</div><div className="text-slate-700">{o.fulfilled_manually ? "Manually" : (o.has_code ? "Auto" : "Not yet")}</div></div>
                                            <div><div className="text-slate-400">Created</div><div className="text-slate-700">{o.created_at ? new Date(o.created_at).toLocaleString() : "-"}</div></div>
                                            <div><div className="text-slate-400">Completed</div><div className="text-slate-700">{o.completed_at ? new Date(o.completed_at).toLocaleString() : "-"}</div></div>
                                            <div><div className="text-slate-400">Price (base)</div><div className="text-slate-700">{Number(o.price||0).toFixed(2)}</div></div>
                                            <div><div className="text-slate-400">Price CAD</div><div className="text-slate-700">{o.price_cad ? `CA$${Number(o.price_cad).toFixed(2)}` : "-"}</div></div>
                                        </div>
                                        {o.ppf_error && <div className="mt-2 text-xs text-rose-600">Error: {o.ppf_error}</div>}
                                    </td>
                                </tr>
                            )}
                            </>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default function AdminPrepaidForge() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/prepaidforge/stats")
            .then(r => { setStats(r.data); setLoading(false); })
            .catch(err => { setError(err.response?.data?.detail || "Failed to load"); setLoading(false); });
    };

    useEffect(() => { load(); }, []);

    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-emerald-500" /></div>;
    if (error) return <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8 text-center text-rose-600">{error}</div>;

    const { total_orders, total_revenue_eur, total_cost_eur, total_profit_eur, total_profit_usd, markup_percent, by_payment_method, top_products, recent_orders, ppf_balance } = stats;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Gift Card Store</h1>
                    <p className="text-sm text-slate-500">PrepaidForge sales and profit tracking</p>
                </div>
                <button onClick={load} className="flex items-center gap-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 px-4 py-2 rounded-full hover:bg-slate-50">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {ppf_balance && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Package size={16} className="text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-700">PrepaidForge Balance</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-700">{ppf_balance.roundedFormattedString}</span>
                </div>
            )}

            <AwaitingStock />
            <PendingETransfers />
            <AllOrders />
            <BuyCardTraffic />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: "Total Orders", value: total_orders, icon: ShoppingBag, color: "blue" },
                    { label: "Total Revenue", value: `€${Number(total_revenue_eur||0).toFixed(2)}`, icon: DollarSign, color: "emerald" },
                    { label: "Total Cost", value: `€${Number(total_cost_eur||0).toFixed(2)}`, icon: DollarSign, color: "amber" },
                    { label: "Total Profit", value: `€${Number(total_profit_eur||0).toFixed(2)}`, icon: TrendingUp, color: "green", sub: `~$${Number(total_profit_usd||0).toFixed(2)} USD` },
                ].map((s, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-card p-5">
                        <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                        {s.sub && <div className="text-xs text-slate-400">{s.sub}</div>}
                        <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Markup Settings</h3>
                <div className="flex items-center gap-4">
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-center">
                        <div className="text-xs text-emerald-600 font-medium">Current Markup</div>
                        <div className="text-2xl font-bold text-emerald-700">{markup_percent}%</div>
                    </div>
                    <div className="text-sm text-slate-500">
                        <p>You buy at wholesale price and sell at markup to customers.</p>
                        <p className="text-emerald-600 font-medium mt-1">To change: update PREPAIDFORGE_MARKUP in .env</p>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5">
                    <h3 className="font-semibold text-slate-900 mb-4">By Payment Method</h3>
                    {!Object.keys(by_payment_method||{}).length ? <p className="text-slate-400 text-sm">No orders yet</p> : (
                        <div className="space-y-3">
                            {Object.entries(by_payment_method||{}).map(([method, data]) => (
                                <div key={method} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                    <div><div className="font-medium text-slate-900 capitalize">{method}</div><div className="text-xs text-slate-500">{data.count} orders</div></div>
                                    <div className="text-right"><div className="font-bold text-slate-900">€{Number(data.revenue||0).toFixed(2)}</div><div className="text-xs text-emerald-600">+€{Number(data.profit||0).toFixed(2)} profit</div></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-5">
                    <h3 className="font-semibold text-slate-900 mb-4">Top Products by Profit</h3>
                    {!top_products?.length ? <p className="text-slate-400 text-sm">No orders yet</p> : (
                        <div className="space-y-3">
                            {top_products.map(([sku, data]) => (
                                <div key={sku} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                    <div><div className="font-medium text-slate-900 text-sm truncate max-w-[180px]">{sku}</div><div className="text-xs text-slate-500">{data.count} sold</div></div>
                                    <div className="text-right"><div className="font-bold text-slate-900 text-sm">€{Number(data.revenue||0).toFixed(2)}</div><div className="text-xs text-emerald-600">+€{Number(data.profit||0).toFixed(2)}</div></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-900">Recent Orders</h3></div>
                {!recent_orders?.length ? <div className="p-8 text-center text-slate-400 text-sm">No orders yet</div> : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <th className="text-left px-5 py-3">Order</th>
                            <th className="text-left px-5 py-3">Product</th>
                            <th className="text-left px-5 py-3">Method</th>
                            <th className="text-right px-5 py-3">Revenue</th>
                            <th className="text-right px-5 py-3">Cost</th>
                            <th className="text-right px-5 py-3">Profit</th>
                        </tr></thead>
                        <tbody>
                            {recent_orders.map(o => (
                                <tr key={o.id} className="border-t border-slate-100">
                                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{o.id}</td>
                                    <td className="px-5 py-3 font-medium text-slate-900 max-w-[150px] truncate">{o.sku}</td>
                                    <td className="px-5 py-3 capitalize text-slate-600">{o.payment_method}</td>
                                    <td className="px-5 py-3 text-right font-semibold">€{Number(o.price||0).toFixed(2)}</td>
                                    <td className="px-5 py-3 text-right text-slate-500">€{Number(o.cost_price||0).toFixed(2)}</td>
                                    <td className="px-5 py-3 text-right font-bold text-emerald-600">+€{Number(o.profit_eur||0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

